import type { TovSourceResponse } from "contracts/api/client-tov/sources.responses";
import type { TovVersionResponse } from "contracts/api/client-tov/version.responses";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { TOV_PROCESSING_SOURCE_STATUSES } from "contracts/enums/tov";
import {
  TOV_GUIDELINE_STATE,
  TOV_SOURCE_LABEL,
  TOV_SOURCE_STATE,
} from "../../enums/tov";
import {
  type GuidelineCard,
  guidelineText,
  hasUnrelatedBrandReference,
  PARSED_SOURCE_STATUSES,
  type SourceDetail,
  type SourceTrust,
  sourceNotEstablished,
  sourceOk,
} from "./guidelineModel";

export type DefinitionCounts = ReturnType<typeof emptyCounts>;

export function emptyCounts() {
  return {
    traits: 0,
    traitBoundaries: 0,
    toneDimensions: 0,
    rules: 0,
    lexicon: 0,
    examples: 0,
    audiences: 0,
    contentPillars: 0,
    journeyStages: 0,
    channels: 0,
    channelPlacements: 0,
    visualGuidelines: 0,
    revisionEvents: 0,
    evidenceBySource: new Map<string, number>(),
  };
}

/** Per-source count of linked evidence across the given cards. */
export function evidenceBySource(cards: GuidelineCard[]): Map<string, number> {
  const bySource = new Map<string, number>();
  for (const card of cards) {
    for (const link of card.evidence) {
      bySource.set(
        link.evidence.sourceId,
        (bySource.get(link.evidence.sourceId) ?? 0) + 1,
      );
    }
  }
  return bySource;
}

/** Counts derived from an already-built card list (no second card rebuild). */
export function definitionCounts(
  version: TovVersionResponse,
  cards: GuidelineCard[],
): DefinitionCounts {
  return {
    traits: version.traits.length,
    traitBoundaries: version.traits.reduce(
      (count, trait) => count + trait.boundaries.length,
      0,
    ),
    toneDimensions: version.toneDimensions.length,
    rules: version.rules.length,
    lexicon: version.lexicon.length,
    examples: version.examples.length,
    audiences: version.audiences.length,
    contentPillars: version.contentPillars.length,
    journeyStages: version.journeyStages.length,
    channels: version.channels.length,
    channelPlacements: version.channels.reduce(
      (count, channel) => count + channel.placements.length,
      0,
    ),
    visualGuidelines: version.visualGuidelines.length,
    revisionEvents: version.revisionEvents.length,
    evidenceBySource: evidenceBySource(cards),
  };
}

export function coverageRows(counts: DefinitionCounts) {
  return [
    { label: "Traits", value: counts.traits },
    { label: "Trait boundaries", value: counts.traitBoundaries },
    { label: "Tone dimensions", value: counts.toneDimensions },
    { label: "Rules", value: counts.rules },
    { label: "Lexicon", value: counts.lexicon },
    { label: "Examples", value: counts.examples },
    { label: "Audiences", value: counts.audiences },
    { label: "Pillars", value: counts.contentPillars },
    { label: "Journeys", value: counts.journeyStages },
    { label: "Channels", value: counts.channels },
    { label: "Channel placements", value: counts.channelPlacements },
    { label: "Visual guidance", value: counts.visualGuidelines },
  ];
}

export function sourceCoverageState(
  sources: TovSourceResponse[] | undefined,
  details: SourceDetail[] | undefined,
): string {
  if (!sources) {
    return "Checking source state";
  }
  if (sources.length === 0) {
    return "No sources";
  }
  const parsed = sources.filter((source) =>
    PARSED_SOURCE_STATUSES.has(source.status),
  ).length;
  const processing = sources.filter((source) =>
    TOV_PROCESSING_SOURCE_STATUSES.includes(source.status),
  ).length;
  const pageCount = details?.reduce(
    (count, detail) => count + detail.parsedPages,
    0,
  );
  const parsedLabel = `${parsed}/${sources.length} parsed`;
  const processingLabel = processing > 0 ? ` / ${processing} processing` : "";
  return pageCount === undefined
    ? `${parsedLabel}${processingLabel}`
    : `${parsedLabel}${processingLabel} / ${pageCount} ${pageCount === 1 ? "page" : "pages"}`;
}

export function traceabilityState(
  version: TovVersionResponse | undefined,
  cards: GuidelineCard[],
): string {
  if (!version) {
    return "Checking guidance";
  }
  if (cards.length === 0) {
    return "Not configured";
  }
  if (
    cards.some((item) => item.sourceState === TOV_SOURCE_STATE.NOT_ESTABLISHED)
  ) {
    return "Source not established";
  }
  if (cards.some((item) => item.sourceState === TOV_SOURCE_STATE.LINKED)) {
    return "Source evidence linked";
  }
  return "No linked evidence";
}

/**
 * Whether the active version's guidance references a brand it should not (its
 * source is a foreign style guide). Scans source titles plus all guideline text
 * for the site's review-flagged terms.
 */
export function sourceTrust(
  site: OverviewResponse["site"] | null,
  sources: TovSourceResponse[],
  version: TovVersionResponse | undefined,
): SourceTrust {
  if (!site || !version) {
    return sourceOk();
  }
  if (sources.length === 0) {
    return sourceNotEstablished();
  }
  const text = [
    ...sources.map((source) => source.title),
    ...guidelineText(version),
  ].join(" ");
  if (!hasUnrelatedBrandReference(site, text)) {
    return sourceOk();
  }
  return {
    state: TOV_GUIDELINE_STATE.REVIEW,
    label: TOV_SOURCE_LABEL.SOURCE_REVIEW_REQUIRED,
    reason:
      "The active guideline version references a source whose ownership does not match the current brand context.",
  };
}

export function statusLabel(
  status: TovVersionResponse["status"] | null,
): string {
  if (!status) {
    return "Not available";
  }
  return status[0].toUpperCase() + status.slice(1);
}

export function sourceCountLabel(
  sources: TovSourceResponse[] | undefined,
): string {
  if (!sources) {
    return "Checking";
  }
  return `${sources.length}`;
}
